/**
 * @file 新建图表浮窗内的游戏选择器（Layer 1 纯 UI）。
 * 视觉对齐经分 GameSelector + 最新 Figma：搜索、分组、hover 子版本。
 */
import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Dropdown, List, SearchBox } from 'tea-component';
import { sortGames } from '@/pages/dashboard/components/Navigation/GameSelector/_utils';
import type { GameInfoType } from '@/pages/dashboard/components/Navigation/GameSelector/types';
import GameRow from './components/GameRow';
import GameSelectTrigger from './components/GameSelectTrigger';
import type { IChartGameSelectProps } from './types';
import { matchGameSearch } from './utils/matchGameSearch';
import './index.scss';

/**
 * 新建图表浮窗游戏选择器。
 *
 * @param props - 选择器属性，见 {@link IChartGameSelectProps}
 *
 * @remarks
 * - 选中叶子 `game_code`，触发器由 `selectedGameOption` 回显 icon + 文案
 * - 下拉 portal 挂载 chart 浮层 overlay，页面滚动时与浮层同步位移
 * - 列表分「最近浏览」「其他」两组；搜索时扁平展示匹配结果
 */
const ChartGameSelect: React.FC<IChartGameSelectProps> = (props) => {
  const {
    gameList,
    recentList,
    selectedGameOption,
    value,
    onChange,
    onOpen,
    onClose,
    listWidth = 400,
    overlayClassName = '',
    name,
    placeholder,
  } = props;

  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const isSearchMode = searchValue.length > 0;

  const searchGameList = useMemo(() => {
    if (!isSearchMode) return [];
    const result = gameList.filter((game) => {
      if (matchGameSearch(game, searchValue)) return true;
      return game?.game_List?.some((subGame) => matchGameSearch(subGame, searchValue));
    });
    return sortGames(result) as GameInfoType[];
  }, [gameList, isSearchMode, searchValue]);

  const recentCodeSet = useMemo(
    () => new Set(recentList.map((game) => game.game_code)),
    [recentList],
  );

  const allGamesExcludeRecent = useMemo(
    () => gameList.filter((game) => !recentCodeSet.has(game.game_code)),
    [gameList, recentCodeSet],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchValue('');
    onClose?.();
  }, [onClose]);

  const handleSelectGame = useCallback((gameCode: string) => {
    onChange(gameCode);
    setSearchValue('');
    setIsOpen(false);
    onClose?.();
  }, [onChange, onClose]);

  const renderSectionTitle = (title: string): React.ReactNode => (
    <List.GroupLabel>{title}</List.GroupLabel>
  );

  const renderGameRows = (list: GameInfoType[]): React.ReactNode => {
    if (!list.length) return null;
    return list.map((gameInfo) => (
      <GameRow
        key={gameInfo.game_code}
        gameInfo={gameInfo}
        searchValue={searchValue}
        selectedGameCode={value}
        onSelectGame={handleSelectGame}
        listWidth={listWidth}
      />
    ));
  };

  const renderListContent = (): React.ReactNode => {
    if (isSearchMode) {
      if (!searchGameList.length) {
        return (
          <div className="dba-new-chart-game-empty">
            {formatMessage({ id: 'common_search_no_data' })}
          </div>
        );
      }
      return renderGameRows(searchGameList);
    }

    return (
      <>
        {recentList.length > 0 && (
          <section className="dba-new-chart-game-section">
            {renderSectionTitle(
              formatMessage({ id: 'dba_new_chart_game_group_recent' }),
            )}
            {renderGameRows(recentList)}
          </section>
        )}
        {allGamesExcludeRecent.length > 0 && (
          <section className="dba-new-chart-game-section">
            {renderSectionTitle(
              formatMessage({ id: 'dba_new_chart_game_group_others' }),
            )}
            {renderGameRows(allGamesExcludeRecent)}
          </section>
        )}
      </>
    );
  };

  return (
    <Dropdown
      className="dba-new-chart-game-select"
      overlayClassName={classNames(
        'dba-new-chart-game-panel-overlay',
        overlayClassName,
      )}
      boxStyle={{
        width: `${listWidth}px`,
        height: 390,
        maxHeight: 390,
      }}
      placementOffset={0}
      appearance="pure"
      clickClose={false}
      closeOnScroll={false}
      positionFixed={false}
      popupContainer={() => (
        document.querySelector('#tea-overlay-root .dba-new-chart-popover-overlay')
          || document.body
      )}
      updateOnChildrenChange
      escapeWithReference
      destroyOnClose
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      button={(
        <GameSelectTrigger
          name={name}
          placeholder={placeholder}
          selectedOption={selectedGameOption}
        />
      )}
    >
      <div
        className="dba-new-chart-game-panel"
        style={{ height: 440 }}
      >
        <div className="dba-new-chart-game-panel__search">
          <SearchBox
            size="full"
            autoComplete="off"
            aria-autocomplete="none"
            placeholder={formatMessage({ id: 'game_selector_search_text' })}
            value={searchValue}
            onChange={(val) => setSearchValue(val?.trim() || '')}
            onClear={() => setSearchValue('')}
          />
        </div>
        <div className="dba-new-chart-game-panel__body">
          {renderListContent()}
        </div>
      </div>
    </Dropdown>
  );
};

export default ChartGameSelect;
